// ---------------------------------------------------------------------------
// Enumerations, defined once here and mirrored as Postgres CHECK constraints.
//
// Deliberately not native Postgres enum types -- altering those is painful
// (PLAN 4.1). If you change a list here, change the matching CHECK in
// supabase/migrations in the same commit.
// ---------------------------------------------------------------------------

/**
 * Door outcomes (docs/campaign-requirements.md § 3.4).
 *
 * The field team asked for three: answered, not home, inaccessible. `vacant`
 * and `wrong_address` are kept behind them because they are how the walk list
 * corrects itself -- a canvasser who finds a demolished house has nowhere else
 * to put that, and the address stays in the universe forever. The visit sheet
 * shows the first three as large buttons and these two as a secondary choice.
 */
export const VISIT_OUTCOMES = [
  'answered',
  'not_home',      // most common by far
  'inaccessible',  // gate, dog, locked building, no-soliciting sign
  'vacant',
  'wrong_address',
] as const;

/**
 * Offered only on `inaccessible`, so the next canvasser knows what to expect.
 *
 * `no_soliciting` is a sign rather than an obstacle -- the door could have been
 * knocked. It sits here anyway, because the door went unworked and the field
 * team's three buttons stay three (docs/campaign-requirements.md § 3.4).
 * Deliberately a per-visit reason and not a household flag: political
 * canvassing is generally outside no-soliciting rules (Watchtower v. Village of
 * Stratton), so honouring a sign is the campaign's policy call, not the
 * schema's -- and a flag would make it once, permanently, for everyone.
 */
export const INACCESSIBLE_REASONS = [
  'dog',
  'gate',
  'no_soliciting',
  'other',
] as const;

/**
 * Support, recorded per person rather than per household.
 *
 * Collapsed from the six-level scale in PLAN 4.1 at the field team's request.
 * The strong/lean distinction is not recoverable afterwards; this was a
 * deliberate call (docs/campaign-requirements.md § 2).
 */
export const SUPPORT_LEVELS = ['support', 'unsure', 'oppose'] as const;

/**
 * Party registration as delivered by the voter file, normalised to codes.
 *
 * AIP is the American Independent Party -- an actual right-wing party, and the
 * one California registrants join most often by mistake, believing the name
 * means unaffiliated. An unaffiliated voter is NPP. Never render AIP as "IND":
 * it tells a canvasser the opposite of the truth.
 */
export const PARTIES = [
  'REP', 'DEM', 'NPP', 'AIP', 'LIB', 'GRN', 'PF', 'WTP', 'OTH',
] as const;

export const GENDERS = ['F', 'M'] as const;

/** Targeting tier as delivered in the file. */
export const VOTER_TIERS = ['lock', 'likely', 'persuadable'] as const;

export const LITERATURE = ['none', 'door_hanger', 'handed_flyer'] as const;

// Offered only on not_home outcomes (PLAN 15.3, resolved yes).
export const BEST_RETURN_TIMES = [
  'morning', 'afternoon', 'evening', 'weekend',
] as const;

export const ROLES = ['canvasser', 'organizer', 'admin'] as const;

/**
 * Address provenance. Every household records where it came from.
 *
 * `l2` was added when the campaign moved to a purchased voter file; PLAN 2.2
 * assumed addresses could only ever be public-GIS derived
 * (docs/campaign-requirements.md § 2).
 */
export const ADDRESS_SOURCES = [
  'nad', 'assessor', 'openaddresses', 'osm', 'manual', 'l2', 'rov',
] as const;

export type VisitOutcome        = (typeof VISIT_OUTCOMES)[number];
export type InaccessibleReason  = (typeof INACCESSIBLE_REASONS)[number];
export type SupportLevel        = (typeof SUPPORT_LEVELS)[number];
export type Party               = (typeof PARTIES)[number];
export type Gender              = (typeof GENDERS)[number];
export type VoterTier           = (typeof VOTER_TIERS)[number];
export type Literature          = (typeof LITERATURE)[number];
export type BestReturnTime      = (typeof BEST_RETURN_TIMES)[number];
export type Role                = (typeof ROLES)[number];
export type AddressSource       = (typeof ADDRESS_SOURCES)[number];

// Display labels. Kept next to the values so a new outcome cannot ship
// without someone deciding what the button says.
export const OUTCOME_LABELS: Record<VisitOutcome, string> = {
  answered:      'Answered',
  not_home:      'Not home',
  inaccessible:  'Inaccessible',
  vacant:        'Vacant',
  wrong_address: 'Wrong address',
};

export const INACCESSIBLE_LABELS: Record<InaccessibleReason, string> = {
  dog:           'Dog',
  gate:          'Big gate',
  no_soliciting: 'No soliciting',
  other:         'Other',
};

export const SUPPORT_LABELS: Record<SupportLevel, string> = {
  support: 'Supports',
  unsure:  'Unsure',
  oppose:  'Does not support',
};

export const PARTY_LABELS: Record<Party, string> = {
  REP: 'Republican',
  DEM: 'Democratic',
  NPP: 'No Party Preference',
  AIP: 'American Independent',
  LIB: 'Libertarian',
  GRN: 'Green',
  PF:  'Peace and Freedom',
  WTP: 'We the People',
  OTH: 'Other',
};

export const TIER_LABELS: Record<VoterTier, string> = {
  lock:        'Lock',
  likely:      'Likely',
  persuadable: 'Persuadable',
};

export const LITERATURE_LABELS: Record<Literature, string> = {
  none:         'None',
  door_hanger:  'Door hanger',
  handed_flyer: 'Handed flyer',
};

export const RETURN_TIME_LABELS: Record<BestReturnTime, string> = {
  morning:   'Morning',
  afternoon: 'Afternoon',
  evening:   'Evening',
  weekend:   'Weekend',
};

export const MAX_NOTE_LENGTH = 280;
